import { z } from "zod";
import { MigrationType } from "../../models/Statistics/MigrationType";
import { ConflictReason } from "../../models/ConflictReason";
import { ConflictResolutionAction } from "../../models/ConflictResolutionAction";
import { CONFLICT_RULE_SCHEMA, ConflictRuleDefinition, WinnerKind } from "../../models/ConflictRule";
import { CONFLICT_FILE_FAMILIES } from "../../models/OrgConflictResolution";

// Strict objects throughout: the server refuses a property it does not know rather than ignoring it, because a
// misspelled condition list would otherwise be dropped and the rule would decide groups its author meant to guard.
// Refusing here too means an imported file never loses a part of itself on its way into the form.
const conditionSchema = z
  .object({
    attribute: z.string(),
    folder: z.string().nullable().optional(),
    operator: z.string(),
    // Null in a built-in rule that leaves a value for each firm to enter; the server asks for it before a preview.
    value: z.string().nullable(),
  })
  .strict();

const ruleSchema = z
  .object({
    schema: z.number().int().default(CONFLICT_RULE_SCHEMA),
    name: z.string().nullable().default(null),
    appliesTo: z
      .object({
        // Only the families the form offers: a folder family would load with no toggle to remove it by.
        families: z
          .array(
            z.nativeEnum(MigrationType).refine((family) => CONFLICT_FILE_FAMILIES.includes(family), {
              message: "This family has no file conflicts",
            })
          )
          .default([]),
        reasons: z.array(z.nativeEnum(ConflictReason)).default([]),
        caseNumbers: z.array(z.string()).nullable().default([]).transform((numbers) => numbers ?? []),
      })
      .strict(),
    winner: z
      .object({
        kind: z.nativeEnum(WinnerKind),
        when: z.array(conditionSchema).nullable().default([]).transform((conditions) => conditions ?? []),
      })
      .strict(),
    otherCopies: z
      .object({
        action: z.nativeEnum(ConflictResolutionAction),
        onlyIf: z.array(conditionSchema).nullable().default([]).transform((conditions) => conditions ?? []),
      })
      .strict(),
    checks: z
      .object({
        copyCount: z
          .object({
            exactly: z.number().int().nullable().optional(),
            atLeast: z.number().int().nullable().optional(),
          })
          .strict()
          .nullable()
          .default(null),
        sameSize: z.boolean().default(false),
        differentFolders: z.string().nullable().default(null),
      })
      .strict()
      .nullable()
      .default({ copyCount: null, sameSize: false, differentFolders: null })
      .transform((checks) => checks ?? { copyCount: null, sameSize: false, differentFolders: null }),
    options: z
      .object({
        allowReplacingCopiesInSharePoint: z.boolean().default(false),
        allowAcceptingDifferentContent: z.boolean().default(false),
      })
      .strict()
      .nullable()
      .default({ allowReplacingCopiesInSharePoint: false, allowAcceptingDifferentContent: false })
      .transform(
        (options) => options ?? { allowReplacingCopiesInSharePoint: false, allowAcceptingDifferentContent: false }
      ),
  })
  .strict();

/** A JSON pointer for a zod issue path, so a refusal names the part of the file the way the server's checks do. */
const pointerOf = (path: PropertyKey[]): string =>
  path.length === 0
    ? "/"
    : path.map((segment) => "/" + String(segment).replace(/~/g, "~0").replace(/\//g, "~1")).join("");

export type ParsedConflictRule =
  | { ok: true; rule: ConflictRuleDefinition }
  | { ok: false; message: string };

/**
 * Reads rule text — an imported file, a preset, a saved rule or a past run's rule — into the shape the form edits.
 * Only the shape is checked here; whether the rule can run is the server's `Validate`, which the form calls next.
 */
export const parseConflictRule = (text: string): ParsedConflictRule => {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return { ok: false, message: "The rule is not valid JSON." };
  }

  const parsed = ruleSchema.safeParse(json);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return {
      ok: false,
      message: `Not a rule this form can show: ${pointerOf(issue?.path ?? [])}: ${issue?.message ?? "unexpected shape"}.`,
    };
  }

  return { ok: true, rule: parsed.data };
};
